import { Page } from "@mb-pro-ui/components";
import { Select } from "@mb-pro-ui/components/form";
import { Grid, Button, Skeleton } from "@mui/material";
import { Form } from "react-final-form";
import { useIntl } from "react-intl";
import { useMutation } from "react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteIcon from "@mui/icons-material/Delete";

import { TemplateHelper } from "../utils/types";
import { useApi } from "../utils/ApiProvider";
import useEditedStyles from "../hooks/useEditedStyles";
import TitledCard from "../components/templates/basic/TitledCard";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { ConfirmDialog, ConfirmDialogProps } from "../components/ConfirmDialog";

import type { paths } from "@helios/toolbox-types";
import { usePageSxProps } from "./styles";
import { TextField } from "../components/form/TextField";
import { APP_NAME } from "../App";
type CreateTemplateHelperResponse = paths["/template-helpers"]["post"]["responses"]["200"]["content"]["application/json"];

const TemplateHelperPage = () => {
  const [dialogProps, setDialogProps] = useState<Omit<ConfirmDialogProps, "open"> | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const formSaveModeRef = useRef<"saveAsNew" | null>(null);

  const { id } = useParams<{ id: string }>();
  const { singlePageSx } = usePageSxProps();
  const { formatMessage } = useIntl();
  const api = useApi();
  const editedStyle = useEditedStyles();
  const history = useHistory();
  const { closeSnackbar } = useSnackbar();

  const keyRuleItems = useMemo(
    () => [
      { value: "all", label: formatMessage({ defaultMessage: "All keys" }) },
      { value: "equals", label: formatMessage({ defaultMessage: "Equals" }) },
      { value: "startsWith", label: formatMessage({ defaultMessage: "Starts with" }) },
    ],
    [formatMessage]
  );
  const languageItems = useMemo(
    () => [
      { value: "hu", label: "Magyar" },
      { value: "en", label: "English" },
    ],
    []
  );
  const isNew = useMemo(() => id === "new", [id]);

  // TODO: további kezelt hibák felsorolása!!!
  const { errorHandler: onError } = useErrorHandler<TemplateHelper>([
    [{ status: 400 }, () => formatMessage({ defaultMessage: "Configuration helper already exists with rule like this." })],
    [
      { status: 401, type: "template-helper" },
      () => formatMessage({ defaultMessage: "You have no rights for configuration helper management." }),
      () => {
        history.push("/");
      },
    ],
    [{ status: 404 }, () => formatMessage({ defaultMessage: "Configuration helper is not found." }), () => history.push("/template-helpers")],
  ]);

  const templateHelperQuery = () => api<TemplateHelper>(`/template-helpers/${id}`);

  const saveQuery = useCallback(
    (helper: TemplateHelper) => {
      closeSnackbar();
      if (isNew || formSaveModeRef.current) {
        const { id: _id, ...helperData } = helper;
        return api(`/template-helpers`, { method: "post", body: JSON.stringify(helperData) });
      }
      return api(`/template-helpers/${id}`, { method: "post", body: JSON.stringify(helper) });
    },
    [api, closeSnackbar, id, isNew]
  );
  const deleteQuery = () => api(`/template-helpers/${id}`, { method: "delete" });

  const { mutate: getTemplateHelperData, data: helperData, isLoading } = useMutation({ mutationFn: templateHelperQuery, onError });

  const onSaveSuccess = useCallback(
    (response: any) => {
      if (isNew || formSaveModeRef.current === "saveAsNew") {
        const { id } = response as CreateTemplateHelperResponse;
        history.push(`/template-helpers/${id}`);
        if (!isNew) {
          formSaveModeRef.current = null;
          getTemplateHelperData();
        }
        return;
      }
      getTemplateHelperData();
    },
    [getTemplateHelperData, history, isNew]
  );
  const { mutate: saveTemplateHelper, isLoading: saving } = useMutation({ mutationFn: saveQuery, onSuccess: onSaveSuccess, onError });
  const { mutate: deleteTemplateHElper, isLoading: deleting } = useMutation({
    mutationFn: deleteQuery,
    onSuccess: () => {
      history.push("/template-helpers");
    },
    onError,
  });

  const saveAndDeleteDisabled = useMemo(() => saving || deleting, [saving, deleting]);

  const askRemoveFile = useCallback(() => {
    setDialogProps({
      title: formatMessage({ defaultMessage: "Delete configuration helper" }),
      message: formatMessage({ defaultMessage: "Are you sure to delete this configuration helper?" }),
      onClose: () => setDialogOpen(false),
      okAction: () => {
        setDialogOpen(false);
        deleteTemplateHElper();
      },
      okLabel: formatMessage({ defaultMessage: "Delete" }),
      color: "error",
    });
    setDialogOpen(true);
  }, [deleteTemplateHElper, formatMessage]);

  const onSubmit = useCallback(
    async (args: TemplateHelper) => {
      saveTemplateHelper(args);
    },
    [saveTemplateHelper]
  );

  const handleSaveAsNew = useCallback(() => {
    formSaveModeRef.current = "saveAsNew";
  }, []);

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "Configuration helpers" })} - ${APP_NAME}`;
  }, [formatMessage]);

  useEffect(() => {
    const unregister = history.listen(() => {
      closeSnackbar();
    });

    return () => {
      unregister();
    };
  }, [history, closeSnackbar]);

  useEffect(() => {
    if (isNew) {
      return;
    }
    getTemplateHelperData();
  }, [getTemplateHelperData, isNew]);

  return (
    <Page sx={singlePageSx}>
      <Form<TemplateHelper>
        onSubmit={onSubmit}
        initialValues={helperData}
        render={({ handleSubmit, dirty }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={1} sx={{ mb: 1 }}>
              <Grid item sx={{ flexGrow: 1 }}></Grid>
              {isNew ? null : (
                <Grid item>
                  <Button color="inherit" startIcon={<DeleteIcon />} sx={{ color: "error.main" }} onClick={askRemoveFile} disabled={saveAndDeleteDisabled}>
                    {formatMessage({ defaultMessage: "Delete" })}
                  </Button>
                </Grid>
              )}
              <Grid item>
                <Button type="submit" onClick={handleSaveAsNew} color="inherit" startIcon={<SaveOutlinedIcon />} disabled={saveAndDeleteDisabled}>
                  {formatMessage({ defaultMessage: "Save as new rule" })}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  color="inherit"
                  startIcon={<SaveOutlinedIcon />}
                  className={dirty ? editedStyle.withHover : ""}
                  disabled={saveAndDeleteDisabled}
                  type="submit"
                >
                  {formatMessage({ defaultMessage: "Save" })}
                </Button>
              </Grid>
            </Grid>
            <TitledCard headerSx={{ display: "none" }} stretch={true}>
              <Grid container spacing={1} sx={{ p: 1 }}>
                {isLoading ? (
                  <>
                    <Grid item xs={12} sm={4} lg={3}>
                      <Skeleton variant="rounded" height={46.56} />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                      <Skeleton variant="rounded" height={46.56} />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                      <Skeleton variant="rounded" height={46.56} />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                      <Skeleton variant="rounded" height={46.56} />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                      <Skeleton variant="rounded" height={46.56} />
                    </Grid>
                    <Grid item xs={12}>
                      <Skeleton variant="rounded" height={135} />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={4} lg={3}>
                      <TextField
                        name="type"
                        required
                        label={formatMessage({ defaultMessage: "Type" })}
                        variant="filled"
                        size="small"
                        fullWidth
                        sx={{ m: 0, minWidth: 50 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                      <Select
                        name="keyRule"
                        required
                        label={formatMessage({ defaultMessage: "Key rule" })}
                        variant="filled"
                        size="small"
                        fullWidth
                        outerSx={{ m: 0 }}
                        items={keyRuleItems}
                        renderOption={({ option }) => (option.value === "all" ? <em>{option.label}</em> : option.label)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                      <TextField
                        name="key"
                        label={formatMessage({ defaultMessage: "Key" })}
                        variant="filled"
                        size="small"
                        fullWidth
                        sx={{ m: 0, minWidth: 50 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                      <Select
                        name="lang"
                        required
                        label={formatMessage({ defaultMessage: "Language" })}
                        variant="filled"
                        size="small"
                        fullWidth
                        outerSx={{ m: 0 }}
                        items={languageItems}
                        renderOption={({ option }) => option.label}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                      <TextField
                        name="hwtype"
                        label={formatMessage({ defaultMessage: "Hardware type" })}
                        variant="filled"
                        size="small"
                        fullWidth
                        sx={{ m: 0, minWidth: 50 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="info"
                        required
                        label={formatMessage({ defaultMessage: "Description" })}
                        variant="filled"
                        size="small"
                        fullWidth
                        sx={{ m: 0 }}
                        multiline
                        minRows={5}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </TitledCard>
          </form>
        )}
      />
      {dialogProps ? <ConfirmDialog open={dialogOpen} {...dialogProps} /> : null}
    </Page>
  );
};

export default TemplateHelperPage;
